import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import '../map/map.css'

export interface SubAreaOption {
  id: string
  label: string
}

export function SettingsSheet({
  open,
  onClose,
  onRefresh,
  refreshPending,
  showBorders,
  onToggleShowBorders,
  subAreas,
  hiddenSubIds,
  onToggleSub,
  heatmapOpacity,
  onHeatmapOpacityChange,
  onSignOut,
}: {
  open: boolean
  onClose: () => void
  onRefresh: () => void
  /** True while a refresh started here is still running. The work happens off
   *  screen — the heatmap re-polls and the areas refetch — so without this the
   *  button looks inert and invites repeated clicking that achieves nothing. */
  refreshPending: boolean
  showBorders: boolean
  onToggleShowBorders: () => void
  subAreas: SubAreaOption[]
  hiddenSubIds: ReadonlySet<string>
  onToggleSub: (id: string) => void
  heatmapOpacity: number
  onHeatmapOpacityChange: (value: number) => void
  onSignOut: () => void
}) {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    // Land keyboard focus inside the sheet the instant it opens, rather than
    // leaving it on the (now covered) button that opened it.
    closeButtonRef.current?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }

    // mousedown, not click: it fires before the settings button's own click
    // handler on the same event, so opening and outside-closing never race,
    // and it reads a drag that starts on the map the same as any other
    // outside interaction — there is no full-screen backdrop to catch it,
    // which is what keeps the map itself pannable while the sheet is open.
    function onOutsidePointerDown(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onOutsidePointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onOutsidePointerDown)
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div ref={panelRef} className="asf-settings-sheet" role="dialog" aria-label={t('settings')}>
      <div className="asf-settings-header">
        <span className="asf-settings-title">{t('settings')}</span>
        <button
          ref={closeButtonRef}
          type="button"
          className="asf-settings-close"
          onClick={onClose}
          aria-label={t('close')}
        >
          ×
        </button>
      </div>

      <button
        type="button"
        className="asf-settings-action"
        onClick={onRefresh}
        disabled={refreshPending}
      >
        {t('refresh_data')}
      </button>

      <label className="asf-settings-toggle">
        <input type="checkbox" checked={showBorders} onChange={onToggleShowBorders} />
        {t('show_borders')}
      </label>

      {subAreas.length > 0 && (
        <div className="asf-settings-subs">
          {subAreas.map((area) => (
            <label key={area.id} className="asf-settings-toggle">
              <input
                type="checkbox"
                checked={!hiddenSubIds.has(area.id)}
                onChange={() => onToggleSub(area.id)}
              />
              {t('show_sub_area', { name: area.label })}
            </label>
          ))}
        </div>
      )}

      <label className="asf-settings-slider">
        <span>{t('heatmap_opacity')}</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={heatmapOpacity}
          onChange={(event) => onHeatmapOpacityChange(Number(event.target.value))}
        />
      </label>

      <button type="button" className="asf-settings-action" onClick={onSignOut}>
        {t('sign_out')}
      </button>
    </div>
  )
}
